import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { Check } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { LimitExhaustedDialog } from '../LimitExhaustedDialog';
import { AgencyPlan, AgencyUsageType } from '@/types/enums';
import { fetchAPI } from '@/api/fetch';
import { apiGetAccountProfile } from '@/api/services/account.service';

export function SuccessSection() {
  const navigate = useNavigate();
  const { candidate, deadline, deadlineTime, reset } = useCreateInterviewAgent();
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);

  const handleGoToDashboard = () => {
    reset();
    navigate('/interview');
  };

  const handleAddASession = async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const agenticInterviewsUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.AgenticInterviews
        );
        const isExhausted = agenticInterviewsUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          reset();
          navigate('/interview/agent/create-session');
        }
      } else {
        // If API call fails, allow navigation
        reset();
        navigate('/interview/agent/create-session');
      }
    } catch {
      // If error occurs, allow navigation
      reset();
      navigate('/interview/agent/create-session');
    }
  };

  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      p="64px 32px"
      bg="background.secondary"
      textAlign="center"
    >
      <Stack gap="32px" alignItems="center">
        {/* Success Icon */}
        <Box position="relative">
          <Box
            w="160px"
            h="160px"
            borderRadius="50%"
            bg="primary.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            {/* Document Icon Background */}
            <Box
              w="80px"
              h="100px"
              bg="neutrals.0"
              borderRadius="8px"
              position="relative"
              border="2px solid"
              borderColor="neutrals.200"
            >
              {/* Document lines */}
              <Stack gap="4px" p="12px" mt="8px">
                <Box h="2px" bg="neutrals.300" w="80%" />
                <Box h="2px" bg="neutrals.300" w="60%" />
                <Box h="2px" bg="neutrals.300" w="70%" />
              </Stack>
            </Box>

            {/* Check mark circle */}
            <Box
              position="absolute"
              bottom="10px"
              right="10px"
              w="50px"
              h="50px"
              bg="primary.600"
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="4px solid"
              borderColor="neutrals.0"
            >
              <Check size={24} color="white" weight="bold" />
            </Box>
          </Box>
        </Box>

        {/* Success Message */}
        <Stack gap="12px" textAlign="center">
          <Text fontSize="32px" fontWeight="bold" color="neutrals.900">
            Automated Interview Created
          </Text>
          <Text fontSize="18px" color="neutrals.600">
            {`You've successfully set up the automated interview for ${
              candidate?.first_name ?? ''
            } with the deadline of ${
              deadline
                ? deadline.toLocaleDateString('default', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
                : ''
            } ${
              deadlineTime
                ? deadlineTime.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })
                : ''
            }.`}
          </Text>
        </Stack>

        {/* Action Buttons */}
        <Flex gap="16px" justifyContent="center" mt="24px">
          <Button
            p="12px 32px"
            fontSize="16px"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            bg="neutrals.0"
            color="primary.900"
            onClick={handleGoToDashboard}
            _hover={{
              bg: 'primary.50'
            }}
          >
            Go to dashboard
          </Button>
          <Button
            p="12px 32px"
            fontSize="16px"
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            onClick={handleAddASession}
            _hover={{
              bg: 'primary.700'
            }}
          >
            Add another session
          </Button>
        </Flex>
      </Stack>
      <LimitExhaustedDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        isFreePlan={isFreePlan}
        usageType="agentic-interview"
      />
    </Box>
  );
}
